import React, { useState, useEffect } from 'react';
import { Edit, Save, X, ToggleLeft, ToggleRight, Plus, Trash2 } from 'lucide-react';
import stripeApiService from '../services/stripeApiService';
import stripeSyncService from '../services/stripeSyncService';
import StripePriceCreator from './StripePriceCreator';

/**
 * Composant pour modifier les produits Stripe existants
 */
const StripeProductsEditor = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Charger les produits Stripe
  const loadStripeProducts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const productsWithPrices = await stripeApiService.getProductsWithPrices();
      setProducts(productsWithPrices);
    } catch (error) {
      console.error('Erreur lors du chargement des produits Stripe:', error);
      setError(`Erreur lors du chargement: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStripeProducts();
  }, []);

  // Commencer l'édition d'un produit
  const startEditProduct = (product) => {
    setEditingProduct({
      ...product,
      originalName: product.name,
      originalDescription: product.description,
      originalActive: product.active,
      originalMetadata: { ...product.metadata }
    });
  };

  // Annuler l'édition
  const cancelEdit = () => {
    setEditingProduct(null);
    setEditingPrice(null);
    setMessage(null);
  };

  // Sauvegarder les modifications d'un produit
  const saveProduct = async () => {
    if (!editingProduct) return;

    setSaving(true);
    setMessage(null);

    try {
      const updateData = {};
      
      if (editingProduct.name !== editingProduct.originalName) {
        updateData.name = editingProduct.name;
      }
      
      if (editingProduct.description !== editingProduct.originalDescription) {
        updateData.description = editingProduct.description;
      }
      
      if (editingProduct.active !== editingProduct.originalActive) {
        updateData.active = editingProduct.active;
      }

      // Gestion des métadonnées
      const metadata = {};
      
      // Catégorie
      if (editingProduct.metadata?.category !== editingProduct.originalMetadata?.category) {
        metadata.category = editingProduct.metadata?.category || '';
      }
      
      // Cible
      if (editingProduct.metadata?.target !== editingProduct.originalMetadata?.target) {
        metadata.target = editingProduct.metadata?.target || '';
      }
      
      // Fonctionnalités
      if (editingProduct.metadata?.features !== editingProduct.originalMetadata?.features) {
        metadata.features = editingProduct.metadata?.features || '';
      }

      // Ajouter les métadonnées si elles ont changé
      if (Object.keys(metadata).length > 0) {
        updateData.metadata = metadata;
      }

      if (Object.keys(updateData).length > 0) {
        await stripeApiService.updateProduct(editingProduct.id, updateData);
        setMessage({ type: 'success', text: 'Produit mis à jour avec succès' });
        
        // Forcer le rafraîchissement du cache pour que les autres composants voient les changements
        await stripeSyncService.getProductsWithForcedRefresh();
        await loadStripeProducts(); // Recharger les données
      } else {
        setMessage({ type: 'info', text: 'Aucune modification détectée' });
      }

      setEditingProduct(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      setMessage({ type: 'error', text: `Erreur lors de la mise à jour: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Activer/désactiver un produit
  const toggleProductActive = async (product) => {
    setSaving(true);
    setMessage(null);

    try {
      await stripeApiService.updateProduct(product.id, { active: !product.active });
      setMessage({ 
        type: 'success', 
        text: `Produit ${!product.active ? 'activé' : 'désactivé'} avec succès` 
      });
      
      // Forcer le rafraîchissement du cache pour que les autres composants voient les changements
      await stripeSyncService.getProductsWithForcedRefresh();
      await loadStripeProducts(); // Recharger les données
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      setMessage({ type: 'error', text: `Erreur: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Activer/désactiver un prix
  const togglePriceActive = async (price) => {
    setSaving(true);
    setMessage(null);

    try {
      await stripeApiService.updatePrice(price.id, { active: !price.active });
      setMessage({ 
        type: 'success', 
        text: `Prix ${!price.active ? 'activé' : 'désactivé'} avec succès` 
      });
      await loadStripeProducts(); // Recharger les données
    } catch (error) {
      console.error('Erreur lors de la modification du prix:', error);
      setMessage({ type: 'error', text: `Erreur: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Formater le prix pour l'affichage
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    
    const amount = price.unit_amount / 100;
    const currency = price.currency.toUpperCase();
    
    if (price.type === 'recurring') {
      const interval = price.recurring.interval;
      const intervalCount = price.recurring.interval_count;
      
      // Traduire les intervalles en français
      const intervalTranslations = {
        'day': 'jour',
        'week': 'semaine',
        'month': 'mois',
        'year': 'an'
      };
      
      const intervalFr = intervalTranslations[interval] || interval;
      
      let intervalText = '';
      if (intervalCount > 1) {
        intervalText = `tous les ${intervalCount} ${intervalFr}s`;
      } else {
        intervalText = `par ${intervalFr}`;
      }
      
      return `${amount} ${currency} ${intervalText}`;
    } else {
      return `${amount} ${currency} (une fois)`;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
          <span className="text-gray-600 dark:text-gray-400">Chargement des produits...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-4">
            Erreur de connexion Stripe
          </h3>
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <button
            onClick={loadStripeProducts}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Edit className="w-8 h-8 text-purple-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Édition des produits Stripe
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Modifiez et gérez vos produits et prix Stripe directement
            </p>
          </div>
        </div>
        <button
          onClick={loadStripeProducts}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <div className="w-4 h-4 mr-2">⟳</div>
          )}
          Actualiser
        </button>
      </div>

      {/* Message de statut */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
            : message.type === 'error'
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
            : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Liste des produits */}
      {products.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
            Aucun produit trouvé
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300">
            Aucun produit n'a été trouvé dans votre compte Stripe.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {products.map((product) => (
            <div key={product.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              {/* En-tête du produit */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {editingProduct?.id === product.id ? (
                    <div className="flex items-center space-x-4">
                      <input
                        type="text"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Nom du produit"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={saveProduct}
                          disabled={saving}
                          className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {saving ? '...' : 'Sauvegarder'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {product.name}
                      </h3>
                      <button
                        onClick={() => startEditProduct(product)}
                        className="ml-4 p-2 text-gray-500 hover:text-purple-600 dark:hover:text-purple-400"
                        title="Modifier le produit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    product.active
                      ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                  }`}>
                    {product.active ? 'Actif' : 'Inactif'}
                  </span>
                  
                  <button
                    onClick={() => toggleProductActive(product)}
                    disabled={saving}
                    className={`flex items-center px-3 py-1 rounded-md text-sm ${
                      product.active
                        ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-700'
                        : 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-700'
                    } disabled:opacity-50`}
                    title={product.active ? 'Désactiver le produit' : 'Activer le produit'}
                  >
                    {product.active ? (
                      <ToggleLeft className="w-4 h-4 mr-1" />
                    ) : (
                      <ToggleRight className="w-4 h-4 mr-1" />
                    )}
                    {product.active ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </div>

              {/* Description */}
              {editingProduct?.id === product.id ? (
                <textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
                  placeholder="Description du produit"
                  rows="3"
                />
              ) : (
                product.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {product.description}
                  </p>
                )
              )}

              {/* Métadonnées - Affichage */}
              {!editingProduct?.id === product.id && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Métadonnées</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Catégorie:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {product.metadata?.category || 'Non définie'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Cible:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {product.metadata?.target || 'Non définie'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Fonctionnalités:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {product.metadata?.features || 'Standard'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Métadonnées - Édition */}
              {editingProduct?.id === product.id && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Configuration des métadonnées</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Catégorie */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Catégorie
                      </label>
                      <select
                        value={editingProduct.metadata?.category || ''}
                        onChange={(e) => setEditingProduct({
                          ...editingProduct,
                          metadata: {
                            ...editingProduct.metadata,
                            category: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Sélectionner une catégorie</option>
                        <option value="subscription">Abonnement</option>
                        <option value="billing">Facturation</option>
                        <option value="service">Service</option>
                      </select>
                    </div>

                    {/* Cible */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Public cible
                      </label>
                      <select
                        value={editingProduct.metadata?.target || ''}
                        onChange={(e) => setEditingProduct({
                          ...editingProduct,
                          metadata: {
                            ...editingProduct.metadata,
                            target: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Sélectionner une cible</option>
                        <option value="employee">Intervenant</option>
                        <option value="beneficiary">Bénéficiaire</option>
                        <option value="both">Les deux</option>
                      </select>
                    </div>

                    {/* Fonctionnalités */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Niveau de fonctionnalités
                      </label>
                      <select
                        value={editingProduct.metadata?.features || ''}
                        onChange={(e) => setEditingProduct({
                          ...editingProduct,
                          metadata: {
                            ...editingProduct.metadata,
                            features: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Standard</option>
                        <option value="premium">Premium</option>
                        <option value="enterprise">Entreprise</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Aide sur les métadonnées */}
                  <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
                      Comment utiliser les métadonnées ?
                    </h5>
                    <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                      <li><strong>Catégorie</strong>: Définit l'usage du produit (abonnement, facturation, service)</li>
                      <li><strong>Public cible</strong>: Définit qui peut utiliser ce produit</li>
                      <li><strong>Fonctionnalités</strong>: Définit le niveau de fonctionnalités</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Prix associés */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Prix ({product.prices.length})
                  </h4>
                  <StripePriceCreator 
                    product={product} 
                    onPriceCreated={loadStripeProducts}
                  />
                </div>
                {product.prices.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Aucun prix configuré pour ce produit
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {product.prices.map((price) => (
                      <div key={price.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {price.nickname || 'Prix standard'}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              price.active
                                ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                            }`}>
                              {price.active ? 'Actif' : 'Inactif'}
                            </span>
                            <button
                              onClick={() => togglePriceActive(price)}
                              disabled={saving}
                              className={`p-1 rounded ${
                                price.active
                                  ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800'
                                  : 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800'
                              } disabled:opacity-50`}
                              title={price.active ? 'Désactiver le prix' : 'Activer le prix'}
                            >
                              {price.active ? (
                                <ToggleLeft className="w-4 h-4" />
                              ) : (
                                <ToggleRight className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatPrice(price)}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          ID: {price.id}
                        </div>
                        {price.type === 'recurring' && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Type: Abonnement récurrent
                          </div>
                        )}
                        <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                          ⚠️ Le montant ne peut pas être modifié
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ID du produit */}
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                ID Stripe: {product.id}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Informations */}
      <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
        <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">
          Fonctionnalités disponibles
        </h4>
        <ul className="text-purple-700 dark:text-purple-300 text-sm space-y-1">
          <li>• <strong>Modification des noms</strong> et descriptions des produits</li>
          <li>• <strong>Activation/désactivation</strong> des produits et prix</li>
          <li>• <strong>Actualisation en temps réel</strong> des données Stripe</li>
          <li>• <strong>Gestion complète</strong> de tous vos produits Stripe</li>
        </ul>
      </div>
    </div>
  );
};

export default StripeProductsEditor;
